import { useEffect, useRef, useState } from "react";
import { ChatCircle, PaperPlaneRight, Trash } from "@phosphor-icons/react";
import { api, extractApiError } from "../lib/api";

/** Real-time(ish) squad chat. Polls every 15s for new comments (not WebSockets
 *  to keep infra simple). Owner/co-owners can delete any comment; authors can
 *  delete their own. */
export default function SquadChat({ squad, currentUserId, isManager }) {
  const [comments, setComments] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const listRef = useRef(null);

  const load = () => {
    api.get(`/squads/${squad.id}/comments`)
      .then((r) => setComments(r.data))
      .catch((e) => setErr(extractApiError(e)));
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 15000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [squad.id]);

  // Auto-scroll to newest on new messages
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [comments.length]);

  const send = async () => {
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/squads/${squad.id}/comments`, { body });
      setComments((c) => [...c, data]);
      setDraft("");
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/squads/${squad.id}/comments/${id}`);
      setComments((c) => c.filter((x) => x.id !== id));
    } catch (e) {
      alert(extractApiError(e));
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return d.toLocaleDateString();
  };

  return (
    <div className="border border-border bg-card flex flex-col" data-testid="squad-chat">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <ChatCircle size={16} weight="fill" className="text-volt" />
        <div className="label-mono text-volt">// SQUAD CHAT</div>
        <div className="text-xs text-muted-foreground ml-auto">
          {comments.length} {comments.length === 1 ? "message" : "messages"}
        </div>
      </div>

      <div ref={listRef} className="px-4 py-3 space-y-3 max-h-[420px] overflow-y-auto" data-testid="comments-list">
        {comments.length === 0 && (
          <div className="text-xs text-muted-foreground text-center py-6 italic">
            No messages yet. Be the first to say something.
          </div>
        )}
        {comments.map((c) => {
          const mine = c.user_id === currentUserId;
          const canDelete = mine || isManager;
          return (
            <div key={c.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`comment-${c.id}`}>
              <div className={`max-w-[80%] ${mine ? "bg-volt/10 border-volt/30" : "bg-background border-border"} border px-3 py-2`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="font-display text-xs font-bold uppercase">{c.author_name}</div>
                  <div className="text-[10px] text-muted-foreground">{fmtTime(c.created_at)}</div>
                  {canDelete && (
                    <button
                      onClick={() => remove(c.id)}
                      className="text-muted-foreground hover:text-destructive ml-1"
                      aria-label="Delete"
                      data-testid={`delete-comment-${c.id}`}
                    >
                      <Trash size={11} weight="bold" />
                    </button>
                  )}
                </div>
                <div className="text-sm whitespace-pre-wrap break-words">{c.body}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-border p-3 flex items-end gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={2}
          placeholder="Drop a message for the squad…"
          maxLength={2000}
          className="flex-1 bg-background border border-border px-3 py-2 text-sm focus:outline-none focus:border-volt resize-none"
          data-testid="comment-input"
        />
        <button
          onClick={send}
          disabled={!draft.trim() || busy}
          className="bg-volt text-black p-3 disabled:opacity-40 hover:opacity-90"
          data-testid="send-comment"
          aria-label="Send"
        >
          <PaperPlaneRight size={16} weight="bold" />
        </button>
      </div>
      {err && <div className="text-red-400 text-xs px-3 pb-2">{err}</div>}
    </div>
  );
}
